"use client"

import React, { useState } from "react"
import { Check, Copy } from "lucide-react"
import { cn } from "@/lib/utils"
import {
    CodeBlock,
    CodeBlockCode,
    CodeBlockGroup,
} from "@/components/ui/prompt-kit/code-block"

export function EnhancedCodeBlock({
    inline = false,
    className,
    children,
    language = "",
    compact = false,
    theme = "github-dark",
}: {
    inline?: boolean
    className?: string
    language?: string
    children: React.ReactNode
    compact?: boolean
    theme?: string
}) {
    const [copied, setCopied] = useState(false)
    const extractText = (node: React.ReactNode): string => {
        if (node == null) return ''
        if (typeof node === 'string' || typeof node === 'number') return String(node)
        if (Array.isArray(node)) return node.map(extractText).join('')
        if (React.isValidElement(node)) {
            if (typeof node.type === 'string' && node.type.toLowerCase() === 'br') return '\n'
            return extractText((node.props as { children?: React.ReactNode }).children)
        }
        return ''
    }

    const content = extractText(children).replace(/\n$/, '')

    const handleCopy = () => {
        navigator.clipboard.writeText(content)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    // Inline
    if (inline) {
        return (
            <code
                className={cn(
                    "relative rounded bg-muted font-mono font-medium",
                    compact ? "px-[0.25rem] py-[0.15rem] text-xs" : "px-[0.3rem] py-[0.2rem] text-xs",
                    className
                )}
            >
                {content}
            </code>
        )
    }

    // Compact preview
    if (compact) {
        return (
            <div className={cn("my-3 bg-secondary/30 rounded p-2 text-sm font-mono border border-border/50", className)}>
                <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-muted-foreground">{language}</span>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleCopy() }}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </button>
                </div>
                <div className="text-sm line-clamp-3">
                    {content}
                </div>
            </div>
        )
    }

    // Full code block using prompt-kit (Shiki under the hood)
    return (
        <div className={cn("w-full my-4 not-prose", className)}>
            <CodeBlock>
                <CodeBlockGroup className="border-border border-b px-2 py-1 bg-muted/30">
                    <div className="flex items-center gap-2">
                        <div className="text-primary rounded px-2 py-1 text-sm font-medium">
                            {language}
                        </div>
                    </div>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleCopy() }}
                        className="h-8 w-8 inline-flex items-center justify-center"
                        aria-label="Copy code"
                    >
                        {copied ? (
                            <Check className="h-4 w-4 text-green-500" />
                        ) : (
                            <Copy className="h-4 w-4" />
                        )}
                    </button>
                </CodeBlockGroup>
                <CodeBlockCode 
                    code={content} 
                    language={language || 'plaintext'} 
                    theme={theme} 
                    className={cn("custom-code-bg")}
                />
            </CodeBlock>
        </div>
    )
}
